import { useMemo, useState } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { FiMaximize2, FiMinimize2 } from 'react-icons/fi';

interface EntryEquityCurve {
  /** Entry identifier (user or strategy name) */
  name: string;
  /** Daily cumulative returns (as decimals, e.g., 0.05 = 5%) */
  returns: number[];
  /** Rank in competition */
  rank: number;
  /** Optional color override */
  color?: string;
}

interface EquityCurveComparisonProps {
  /** Array of entry equity curves to compare */
  entries: EntryEquityCurve[];
  /** Start date of the competition */
  startDate?: string;
  /** Chart height in pixels */
  height?: number;
  /** Show log scale toggle */
  showLogToggle?: boolean;
  /** Max entries to show (default 5) */
  maxEntries?: number;
  /** Optional benchmark returns */
  benchmark?: {
    name: string;
    returns: number[];
  };
}

// Colorblind-safe palette from ColorBrewer
// Distinct colors that work for most color vision deficiencies
const COLORBLIND_PALETTE = [
  '#2166ac', // Blue (rank 1)
  '#d6604d', // Red-orange (rank 2)
  '#4dac26', // Green (rank 3)
  '#b2abd2', // Purple-gray (rank 4)
  '#fdb863', // Orange-yellow (rank 5)
  '#1b7837', // Dark green
  '#762a83', // Purple
  '#91bfdb', // Light blue
  '#dfc27d', // Tan
  '#80cdc1', // Teal
];

/**
 * EquityCurveComparison - Multi-series equity curve chart.
 *
 * Compares equity curves of top competition entries.
 * Features:
 * - Colorblind-safe palette
 * - Log scale toggle
 * - Hover to highlight single series
 * - Fullscreen expansion
 * - Benchmark comparison (optional)
 */
export default function EquityCurveComparison({
  entries,
  startDate,
  height = 300,
  showLogToggle = true,
  maxEntries = 5,
  benchmark,
}: EquityCurveComparisonProps) {
  const [useLogScale, setUseLogScale] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hoveredEntry, setHoveredEntry] = useState<string | null>(null);

  // Limit entries and ensure they have colors
  const displayEntries = useMemo(() => {
    return entries.slice(0, maxEntries).map((entry, i) => ({
      ...entry,
      color: entry.color || COLORBLIND_PALETTE[i % COLORBLIND_PALETTE.length],
    }));
  }, [entries, maxEntries]);

  // Convert returns to chart data
  // Each point has a date/index and cumulative return for each entry
  const chartData = useMemo(() => {
    if (displayEntries.length === 0) return [];

    // Find the max length among all entries
    const maxLength = Math.max(
      ...displayEntries.map((e) => e.returns.length),
      benchmark?.returns.length ?? 0
    );

    const data = [];
    for (let i = 0; i < maxLength; i++) {
      const point: Record<string, number | string> = {
        index: i + 1,
        date: startDate
          ? new Date(new Date(startDate).getTime() + i * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
          : `Day ${i + 1}`,
      };

      // Add each entry's cumulative return
      for (const entry of displayEntries) {
        if (i < entry.returns.length) {
          // Calculate cumulative return from daily returns
          const cumulativeReturn = entry.returns.slice(0, i + 1).reduce((acc, r) => acc * (1 + r), 1) - 1;
          point[entry.name] = useLogScale
            ? Math.log10(1 + cumulativeReturn) * 100 // Log scale
            : cumulativeReturn * 100; // Percentage
        }
      }

      // Add benchmark if present
      if (benchmark && i < benchmark.returns.length) {
        const benchmarkCumulative = benchmark.returns.slice(0, i + 1).reduce((acc, r) => acc * (1 + r), 1) - 1;
        point[benchmark.name] = useLogScale
          ? Math.log10(1 + benchmarkCumulative) * 100
          : benchmarkCumulative * 100;
      }

      data.push(point);
    }

    return data;
  }, [displayEntries, benchmark, startDate, useLogScale]);

  const formatYAxis = (value: number) => {
    if (useLogScale) {
      // Convert back from log scale for display
      const actualReturn = Math.pow(10, value / 100) - 1;
      return `${(actualReturn * 100).toFixed(0)}%`;
    }
    return `${value.toFixed(0)}%`;
  };

  const formatTooltip = (value: number) => {
    if (useLogScale) {
      const actualReturn = Math.pow(10, value / 100) - 1;
      return `${(actualReturn * 100).toFixed(2)}%`;
    }
    return `${value.toFixed(2)}%`;
  };

  if (displayEntries.length === 0) {
    return (
      <div
        className="flex items-center justify-center bg-gray-900 border border-gray-800 rounded-md"
        style={{ height }}
      >
        <p className="text-gray-500">No equity curves to display</p>
      </div>
    );
  }

  const chartContent = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-800">
        <h3 className="text-sm font-medium text-white">Equity Curve Comparison</h3>
        <div className="flex items-center gap-2">
          {showLogToggle && (
            <button
              onClick={() => setUseLogScale(!useLogScale)}
              className={`px-2 py-1 text-xs rounded transition-colors ${
                useLogScale
                  ? 'bg-profit/20 text-profit'
                  : 'bg-gray-800 text-gray-400 hover:text-white'
              }`}
            >
              Log Scale
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 text-gray-400 hover:text-white transition-colors"
          >
            {isExpanded ? (
              <FiMinimize2 className="w-4 h-4" />
            ) : (
              <FiMaximize2 className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="px-2 py-4" style={{ height: isExpanded ? 500 : height }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <XAxis
              dataKey="date"
              tick={{ fill: '#6b7280', fontSize: 11 }}
              axisLine={{ stroke: '#374151' }}
              tickLine={{ stroke: '#374151' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tickFormatter={formatYAxis}
              tick={{ fill: '#6b7280', fontSize: 11 }}
              axisLine={{ stroke: '#374151' }}
              tickLine={{ stroke: '#374151' }}
              width={50}
            />
            <ReferenceLine y={0} stroke="#4b5563" strokeDasharray="3 3" />
            <Tooltip
              contentStyle={{
                background: '#1f2937',
                border: '1px solid #374151',
                borderRadius: '6px',
                padding: '8px 12px',
              }}
              labelStyle={{ color: '#9ca3af', marginBottom: '4px' }}
              formatter={(value: number, name: string) => [formatTooltip(value), name]}
            />
            <Legend
              wrapperStyle={{ paddingTop: '10px' }}
              formatter={(value) => <span className="text-xs text-gray-400">{value}</span>}
            />

            {/* Benchmark line (dashed) */}
            {benchmark && (
              <Line
                type="monotone"
                dataKey={benchmark.name}
                stroke="#6b7280"
                strokeWidth={1.5}
                strokeDasharray="5 5"
                dot={false}
                opacity={hoveredEntry && hoveredEntry !== benchmark.name ? 0.2 : 1}
                onMouseEnter={() => setHoveredEntry(benchmark.name)}
                onMouseLeave={() => setHoveredEntry(null)}
              />
            )}

            {/* Entry lines */}
            {displayEntries.map((entry) => (
              <Line
                key={entry.name}
                type="monotone"
                dataKey={entry.name}
                stroke={entry.color}
                strokeWidth={entry.rank === 1 ? 2.5 : 1.5}
                dot={false}
                opacity={hoveredEntry && hoveredEntry !== entry.name ? 0.2 : 1}
                onMouseEnter={() => setHoveredEntry(entry.name)}
                onMouseLeave={() => setHoveredEntry(null)}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Legend with rank indicators */}
      <div className="px-4 pb-3 flex flex-wrap gap-3">
        {displayEntries.map((entry) => (
          <div
            key={entry.name}
            className={`flex items-center gap-2 text-xs transition-opacity ${
              hoveredEntry && hoveredEntry !== entry.name ? 'opacity-30' : 'opacity-100'
            }`}
            onMouseEnter={() => setHoveredEntry(entry.name)}
            onMouseLeave={() => setHoveredEntry(null)}
          >
            <span
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-400">
              #{entry.rank} {entry.name}
            </span>
          </div>
        ))}
        {benchmark && (
          <div
            className={`flex items-center gap-2 text-xs transition-opacity ${
              hoveredEntry && hoveredEntry !== benchmark.name ? 'opacity-30' : 'opacity-100'
            }`}
            onMouseEnter={() => setHoveredEntry(benchmark.name)}
            onMouseLeave={() => setHoveredEntry(null)}
          >
            <span className="w-3 h-0.5 bg-gray-500" style={{ background: 'repeating-linear-gradient(90deg, #6b7280 0, #6b7280 4px, transparent 4px, transparent 8px)' }} />
            <span className="text-gray-500">{benchmark.name}</span>
          </div>
        )}
      </div>
    </>
  );

  // Expanded fullscreen view
  if (isExpanded) {
    return (
      <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-8">
        <div className="w-full max-w-6xl bg-gray-900 border border-gray-800 rounded-lg">
          {chartContent}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md overflow-hidden">
      {chartContent}
    </div>
  );
}
